import { GeoPoint, Timestamp } from 'firebase/firestore';
import { normalizeLocalServerUrl, checkLanBrowserSecurity } from '../utils/localServerAddress';
/*
 * Company/store backend router.
 *
 * Modes:
 *  - online_only: Firebase/online backend only.
 *  - offline_server_only: local store gateway is authoritative for the UI.
 *  - sqlite_offline: SQLite-backed gateway stays authoritative even without internet.
 *    This mode uses the existing Node server's frostflow.sqlite database,
 *    never a second browser-only database. Cloud sync is separate/optional.
 *  - hybrid: local-first, with explicit cloud fallback when the gateway is down.
 *
 * The configuration is persisted on the tenant under operationalSettings.offline
 * and cached in localStorage so the router is available before Settings is opened.
 */

export const SERVER_MODES = Object.freeze({
  ONLINE_ONLY: 'online_only',
  OFFLINE_SERVER_ONLY: 'offline_server_only',
  SQLITE_OFFLINE: 'sqlite_offline',
  HYBRID: 'hybrid',
});

export const DEFAULT_SERVER_CONFIG = Object.freeze({
  serverMode: SERVER_MODES.ONLINE_ONLY,
  localServer: {
    enabled: false,
    url: '',
    storeId: '',
    serverId: '',
    token: '',
    timeoutMs: 3000,
    autoDiscovery: true,
    serverName: '',
    lanHostname: 'frostflow.local',
    preferredInterface: '',
    allowLanClients: true,
    requireDeviceApproval: true,
  },
  cloud: {
    enabled: true,
    allowFallback: true,
  },
  hybrid: {
    preferredSource: 'local',
    switchBackAutomatically: true,
  },
  sync: {
    enabled: true,
    automatic: true,
    intervalSeconds: 30,
    pullIntervalSeconds: 300,
    syncFiles: true,
    backgroundSync: true,
    uploadLocal: true,
    downloadCloud: true,
  },
  backup: {
    enabled: true,
    hour: 23,
    keepDaily: 14,
    keepWeekly: 8,
    keepMonthly: 12,
    includeFiles: false,
    mirrorPath: '',
  },
  security: {
    requireDeviceApproval: true,
    offlineLoginValidityDays: 30,
  },
  files: {
    storeLocallyFirst: true,
    keepLocalAfterSync: true,
    retentionDays: 0,
  },
});

const CONFIG_KEY = 'coldstorage.infrastructure.v1';
const STATUS_EVENT = 'coldstorage:infrastructure-status';
const ADMIN_TOKEN_KEY = 'coldstorage.localServerAdminToken.v1';
const DATA_TOKEN_KEY = 'coldstorage.localServerPairingToken.v1';
const LOCAL_AUTH_SESSION_KEY = 'coldstorage.localAuthSession.v1';
const nowIso = () => new Date().toISOString();
const hasWindow = () => typeof window !== 'undefined';

const safeJson = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(safeJson);
  if (value instanceof Date) return { __firestoreTimestamp: value.toISOString() };
  if (typeof value?.toDate === 'function') {
    try { return { __firestoreTimestamp: value.toDate().toISOString() }; } catch (_) {}
  }
  if (typeof value === 'object') {
    // Preserve Firebase field transforms so the LAN database applies the same
    // intent instead of serializing private SDK objects as empty maps.
    const method = String(value?._methodName || value?.methodName || '');
    if (method === 'serverTimestamp') return { __firestoreTransform: 'serverTimestamp' };
    if (method === 'deleteField') return { __firestoreTransform: 'deleteField' };
    if (method === 'increment') return { __firestoreTransform: 'increment', operand: safeJson(value?._operand ?? value?.operand ?? 0) };
    if (method === 'arrayUnion') return { __firestoreTransform: 'arrayUnion', elements: safeJson(value?._elements || value?.elements || []) };
    if (method === 'arrayRemove') return { __firestoreTransform: 'arrayRemove', elements: safeJson(value?._elements || value?.elements || []) };
    if (typeof value.path === 'string' && value.path) return { __firestoreRef: value.path };
    if (typeof value.latitude === 'number' && typeof value.longitude === 'number') {
      return { __firestoreGeoPoint: { latitude: value.latitude, longitude: value.longitude } };
    }
    const result = {};
    Object.entries(value).forEach(([key, current]) => {
      if (typeof current !== 'function' && current !== undefined) result[key] = safeJson(current);
    });
    return result;
  }
  return value;
};

const reviveJson = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(reviveJson);
  if (typeof value !== 'object') return value;
  if (value.__firestoreTimestamp) {
    try { return Timestamp.fromDate(new Date(value.__firestoreTimestamp)); } catch (_) { return value.__firestoreTimestamp; }
  }
  if (value.__firestoreGeoPoint) {
    try { return new GeoPoint(Number(value.__firestoreGeoPoint.latitude || 0), Number(value.__firestoreGeoPoint.longitude || 0)); } catch (_) { return value.__firestoreGeoPoint; }
  }
  if (value.__firestoreRef) return { path: value.__firestoreRef, id: String(value.__firestoreRef).split('/').filter(Boolean).pop() || '' };
  return Object.fromEntries(Object.entries(value).map(([key, current]) => [key, reviveJson(current)]));
};

const mergeConfig = (value = {}) => ({
  ...DEFAULT_SERVER_CONFIG,
  ...(value || {}),
  localServer: { ...DEFAULT_SERVER_CONFIG.localServer, ...(value?.localServer || {}) },
  cloud: { ...DEFAULT_SERVER_CONFIG.cloud, ...(value?.cloud || {}) },
  hybrid: { ...DEFAULT_SERVER_CONFIG.hybrid, ...(value?.hybrid || {}) },
  sync: { ...DEFAULT_SERVER_CONFIG.sync, ...(value?.sync || {}) },
  backup: { ...DEFAULT_SERVER_CONFIG.backup, ...(value?.backup || {}) },
  security: { ...DEFAULT_SERVER_CONFIG.security, ...(value?.security || {}) },
  files: { ...DEFAULT_SERVER_CONFIG.files, ...(value?.files || {}) },
});

const normalizeBaseUrl = (value = '') => normalizeLocalServerUrl(value);

const PLACEHOLDER_LOCAL_URL = 'http://192.168.1.10:8080';
const LOCAL_FAILURE_BASE_COOLDOWN_MS = 15000;
const LOCAL_FAILURE_MAX_COOLDOWN_MS = 60000;

const isLocalConfiguredValue = (config = {}) => {
  const local = config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  if (local.enabled === false || !base) return false;
  const hasIdentity = Boolean(
    String(local.storeId || '').trim() ||
    String(local.serverId || '').trim() ||
    String(local.token || '').trim()
  );
  // Old builds populated the LAN placeholder for every tenant. Treat that
  // untouched placeholder as "not configured" so existing online tenants do
  // not hammer 192.168.1.10 and fail every Firestore read.
  if (base === PLACEHOLDER_LOCAL_URL && !hasIdentity) return false;
  return true;
};

let lastLocalTestAt = 0;
let localTestInFlight = null;
let localUnavailableUntil = 0;
let consecutiveLocalFailures = 0;

// Runtime-only choice made by the verified login flow.  It is intentionally
// NOT written to localStorage or tenant settings: a saved LAN mode must never
// hijack a Firebase login, and a cloud login must not erase LAN configuration.
// 'cloud' = Firebase login / Super Admin; 'local' = authenticated LAN login;
// 'auto' = explicit opt-in through Company Settings.
let runtimeBackend = 'cloud';
let runtimePrincipal = '';

let state = {
  config: mergeConfig(),
  tenantId: '',
  local: { online: false, checkedAt: '', latencyMs: null, error: '' },
  cloud: { online: typeof navigator === 'undefined' ? true : navigator.onLine !== false },
  activeSource: 'cloud',
  lastError: '',
};

const loadCached = () => {
  if (!hasWindow()) return;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONFIG_KEY) || '{}');
    state = {
      ...state,
      tenantId: String(parsed.tenantId || state.tenantId || ''),
      config: mergeConfig(parsed.config || parsed || {}),
    };
  } catch (_) {}
};
loadCached();

const configuredMode = () => state.config.serverMode || SERVER_MODES.ONLINE_ONLY;
const isLocalConfigured = () => isLocalConfiguredValue(state.config);
const circuitOpen = () =>
  configuredMode() === SERVER_MODES.HYBRID &&
  localUnavailableUntil > Date.now();

const effectiveMode = () => {
  if (runtimeBackend === 'cloud') return SERVER_MODES.ONLINE_ONLY;
  const current = configuredMode();
  if (!isLocalConfigured()) return SERVER_MODES.ONLINE_ONLY;
  return current;
};

const snapshot = () => ({
  ...state,
  config: mergeConfig(state.config),
  configuredMode: configuredMode(),
  effectiveMode: effectiveMode(),
  localConfigured: isLocalConfigured(),
  localCircuitOpen: circuitOpen(),
  localUnavailableUntil: localUnavailableUntil || 0,
  runtimeBackend,
  localRoutingEnabled:
    runtimeBackend !== 'cloud' && isLocalConfigured() &&
    (configuredMode() === SERVER_MODES.OFFLINE_SERVER_ONLY ||
      configuredMode() === SERVER_MODES.SQLITE_OFFLINE ||
      configuredMode() === SERVER_MODES.HYBRID) && !circuitOpen(),
});

const emit = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: snapshot() }));
};

const cache = () => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(CONFIG_KEY, JSON.stringify({ tenantId: state.tenantId, config: state.config }));
  } catch (_) {}
};

const abortableFetch = async (url, options = {}, timeoutMs = 3000) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), Math.max(500, Number(timeoutMs) || 3000)) : null;
  try {
    return await fetch(url, { ...options, ...(controller ? { signal: controller.signal } : {}) });
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const readLocalSession = () => {
  if (!hasWindow()) return null;
  try { return JSON.parse(window.localStorage.getItem(LOCAL_AUTH_SESSION_KEY) || 'null'); } catch (_) { return null; }
};
// The one-time pairing credential is workstation-local, never part of tenant
// settings synchronized to Firebase or other browsers.
const keepLocalPairingToken = (token, { tenantId = state.tenantId, serverId = state.config.localServer?.serverId, serverUrl = state.config.localServer?.url } = {}) => {
  if (!hasWindow() || !token) return;
  try {
    window.localStorage.setItem(DATA_TOKEN_KEY, JSON.stringify({ token: String(token), tenantId: String(tenantId || ''), serverId: String(serverId || ''), serverUrl: normalizeBaseUrl(serverUrl || '') }));
  } catch (_) {}
};
const readLocalPairingToken = () => {
  if (!hasWindow()) return '';
  try {
    const record = JSON.parse(window.localStorage.getItem(DATA_TOKEN_KEY) || 'null');
    if (!record?.token || !record?.tenantId || !record?.serverId) return '';
    if (String(record.tenantId) !== state.tenantId || String(record.serverId) !== String(state.config.localServer?.serverId || '') || normalizeBaseUrl(record.serverUrl || '') !== normalizeBaseUrl(state.config.localServer?.url || '')) return '';
    return String(record.token);
  } catch (_) { return ''; }
};
const detachLegacyCloudToken = (config, tenantId) => {
  if (!config?.localServer?.token) return config;
  // Support upgrades from older installations, but stop copying the secret
  // into Firestore via the company operational settings UI.
  keepLocalPairingToken(config.localServer.token, { tenantId, serverId: config.localServer.serverId, serverUrl: config.localServer.url });
  return { ...config, localServer: { ...config.localServer, token: '' } };
};
const readAdminToken = () => {
  if (!hasWindow()) return '';
  try { return String(window.localStorage.getItem(ADMIN_TOKEN_KEY) || ''); } catch (_) { return ''; }
};

const deviceIdentity = () => {
  if (!hasWindow()) return { id: '', name: 'Web Browser' };
  let id = '';
  try {
    id = String(window.localStorage.getItem('coldstorage.deviceId') || '');
    if (!id) {
      id = globalThis.crypto?.randomUUID?.() || `WEB-${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
      window.localStorage.setItem('coldstorage.deviceId', id);
    }
  } catch (_) {}
  let name = '';
  try { name = String(window.localStorage.getItem('coldstorage.deviceName') || ''); } catch (_) {}
  if (!name) name = String(navigator?.platform || navigator?.userAgent || 'Web Browser').slice(0,120);
  return { id, name };
};

const authHeaders = () => {
  const local = state.config.localServer || {};
  const session = readLocalSession();
  const sessionIsValid = session?.sessionToken && (!session.expiresAt || Date.parse(session.expiresAt) > Date.now()) &&
    (!session.serverUrl || normalizeBaseUrl(session.serverUrl) === normalizeBaseUrl(local.url)) &&
    (!session.tenantId || session.tenantId === state.tenantId);
  // Prefer individual employee permissions and device-approval sessions over
  // the legacy shared data token. Only the one-time administrator retains the
  // admin token, stored separately on that browser.
  const accessToken = String((sessionIsValid ? session.sessionToken : '') || readLocalPairingToken() || '');
  return {
    'Content-Type': 'application/json',
    'X-Store-Id': String(local.storeId || ''),
    'X-Server-Id': String(local.serverId || ''),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(state.tenantId ? { 'X-Tenant-Id': state.tenantId } : {}),
    ...(hasWindow() ? (() => { const device = deviceIdentity(); return { 'X-Device-Id': device.id, 'X-Device-Name': device.name }; })() : {}),
  };
};

const localRequest = async (path, options = {}) => {
  const local = state.config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  const { forceLocalProbe = false, requestTimeoutMs = null, ...fetchOptions } = options || {};
  if (!local.enabled || !base) throw new Error('Local store server is not configured.');

  if (
    configuredMode() === SERVER_MODES.HYBRID &&
    !forceLocalProbe &&
    circuitOpen()
  ) {
    const error = new Error('Local store server temporarily unavailable; using cloud fallback.');
    error.code = 'local-circuit-open';
    throw error;
  }

  const started = Date.now();
  try {
    const response = await abortableFetch(`${base}${path}`, {
      ...fetchOptions,
      headers: { ...authHeaders(), ...(fetchOptions.headers || {}) },
    }, requestTimeoutMs ?? local.timeoutMs);
    const text = await response.text();
    let body = null;
    try { body = text ? JSON.parse(text) : null; } catch (_) { body = { message: text }; }
    if (!response.ok) {
      const error = new Error(body?.message || body?.error || `Local server returned HTTP ${response.status}`);
      error.code = String(body?.error || `local-http-${response.status}`);
      error.status = response.status;
      error.details = body;
      throw error;
    }

    consecutiveLocalFailures = 0;
    localUnavailableUntil = 0;
    state.local = { online: true, checkedAt: nowIso(), latencyMs: Date.now() - started, error: '' };
    state.lastError = '';
    emit();
    return body;
  } catch (error) {
    consecutiveLocalFailures += 1;
    const cooldown = Math.min(
      LOCAL_FAILURE_MAX_COOLDOWN_MS,
      LOCAL_FAILURE_BASE_COOLDOWN_MS * Math.max(1, consecutiveLocalFailures)
    );
    if (configuredMode() === SERVER_MODES.HYBRID && error?.status !== 401 && error?.status !== 403) {
      localUnavailableUntil = Date.now() + cooldown;
    }
    state.local = { online: false, checkedAt: nowIso(), latencyMs: null, error: String(error?.message || error) };
    state.lastError = state.local.error;
    emit();
    throw error;
  }
};


const adminRequest = async (path, options = {}) => {
  const adminToken = readAdminToken();
  if (!adminToken) throw new Error('This browser has not been paired as a Local Server administrator. Pair the server from Settings first.');
  return localRequest(path, {
    ...options,
    forceLocalProbe: true,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${adminToken}` },
  });
};

const mode = () => configuredMode();
const localFirst = () =>
  runtimeBackend !== 'cloud' && isLocalConfigured() &&
  (mode() === SERVER_MODES.OFFLINE_SERVER_ONLY || mode() === SERVER_MODES.SQLITE_OFFLINE || mode() === SERVER_MODES.HYBRID);
// Hybrid reads always have a safe cloud fallback when cloud access is enabled.
// This keeps branding/settings/labels/navigation readable even when a local
// gateway is down. Write fallback remains separately controllable to avoid
// unintended split-brain operational writes.
const canCloudReadFallback = () =>
  mode() === SERVER_MODES.HYBRID &&
  state.config.cloud?.enabled !== false;
const canCloudFallback = () =>
  canCloudReadFallback() &&
  state.config.cloud?.allowFallback !== false;
const shouldSkipLocalReadForCloud = () =>
  canCloudReadFallback() &&
  (!isLocalConfigured() || circuitOpen());
const shouldSkipLocalWriteForCloud = () =>
  canCloudFallback() &&
  (!isLocalConfigured() || circuitOpen());

const service = {
  EVENT: STATUS_EVENT,
  SERVER_MODES,
  DEFAULT_SERVER_CONFIG,

  getSnapshot: snapshot,
  isLocalConfigured,
  getRoutingScope() {
    return runtimeBackend === 'cloud'
      ? `firebase-cloud:${runtimePrincipal}`
      : `lan:${String(state.tenantId || '')}:${normalizeBaseUrl(state.config.localServer?.url || '')}:${mode()}:${runtimePrincipal}`;
  },
  setRuntimeBackend(backend, principalId) {
    if (!['cloud', 'local', 'auto'].includes(backend)) throw new Error('Invalid backend selection.');
    const nextPrincipal = principalId === undefined ? runtimePrincipal : String(principalId || '');
    if (runtimeBackend !== backend || runtimePrincipal !== nextPrincipal) {
      runtimeBackend = backend;
      runtimePrincipal = nextPrincipal;
      if (backend === 'cloud') state.activeSource = 'cloud';
      emit();
    }
    return snapshot();
  },
  canCloudFallback,
  canCloudReadFallback,
  shouldUseLocal() {
    return localFirst() && !circuitOpen();
  },

  subscribe(listener) {
    if (!hasWindow()) return () => {};
    const handler = (event) => listener(event?.detail || snapshot());
    const online = () => {
      state.cloud.online = true;
      emit();
      if (localFirst()) this.testLocal({ silent: true });
    };
    const offline = () => { state.cloud.online = false; emit(); };
    window.addEventListener(STATUS_EVENT, handler);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    listener(snapshot());
    return () => {
      window.removeEventListener(STATUS_EVENT, handler);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  },

  setTenantContext(tenantId, offlineConfig = {}) {
    const nextTenant = String(tenantId || state.tenantId || '');
    const previousLocal = state.tenantId === nextTenant ? state.config.localServer : null;
    let nextConfig = mergeConfig(offlineConfig);
    // A workstation may only know its local IP, while the cloud-side settings
    // were saved before the LAN server was installed. Preserve that local IP
    // for the SAME tenant; never carry credentials over to another tenant.
    if (previousLocal?.url && !nextConfig.localServer?.url) {
      nextConfig.localServer = { ...nextConfig.localServer, ...previousLocal };
    }
    // During LAN login, cached/old Firestore company settings may still say
    // Online Only. Do not switch a signed-in offline employee back to Firebase
    // just because the Settings provider refreshed the same tenant record.
    const session = readLocalSession();
    const sessionActive = state.tenantId === nextTenant && session?.sessionToken &&
      String(session.tenantId || '') === nextTenant &&
      (!session.expiresAt || Date.parse(session.expiresAt) > Date.now());
    if (sessionActive && state.config.serverMode === SERVER_MODES.SQLITE_OFFLINE &&
        normalizeBaseUrl(session.serverUrl) === normalizeBaseUrl(state.config.localServer?.url)) {
      // An authenticated local SQLite session is workstation-specific. Loading
      // cloud tenant settings must not suddenly reroute it into Firebase or a
      // different LAN gateway (and must not copy tokens between servers).
      nextConfig.serverMode = SERVER_MODES.SQLITE_OFFLINE;
      nextConfig.localServer = { ...nextConfig.localServer, ...state.config.localServer, enabled: true, url: session.serverUrl };
    } else if (sessionActive && state.config.serverMode === SERVER_MODES.HYBRID &&
               nextConfig.serverMode === SERVER_MODES.ONLINE_ONLY) {
      nextConfig.serverMode = SERVER_MODES.HYBRID;
    }
    state.tenantId = nextTenant;
    state.config = detachLegacyCloudToken(nextConfig, nextTenant);
    state.cloud.online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    cache();
    emit();
    return snapshot();
  },

  setConfig(config = {}) {
    state.config = detachLegacyCloudToken(mergeConfig(config), state.tenantId);
    cache();
    emit();
    return snapshot();
  },

  async testLocal({ silent = false, force = false } = {}) {
    if (mode() === SERVER_MODES.ONLINE_ONLY || !isLocalConfigured()) {
      state.activeSource = 'cloud';
      state.local = {
        ...state.local,
        online: false,
        checkedAt: state.local.checkedAt || '',
        error: mode() === SERVER_MODES.HYBRID ? 'Local store server is not configured.' : '',
      };
      emit();
      return snapshot();
    }
    if (!force && lastLocalTestAt && Date.now() - lastLocalTestAt < 15000) {
      return snapshot();
    }
    if (!force && localTestInFlight) return localTestInFlight;

    const run = (async () => {
      try {
        // /health is public and cannot prove that reads/writes will work.
        // /v1/status confirms the tenant and client credentials are valid.
        const details = await localRequest('/v1/status', { method: 'GET', forceLocalProbe: Boolean(force) });
        if (state.tenantId && details?.server?.tenantId && details.server.tenantId !== state.tenantId) {
          throw new Error('This server belongs to a different company.');
        }
        state.activeSource = 'local';
        state.local = { ...state.local, online: true, details: details || null };
      } catch (error) {
        state.activeSource = canCloudReadFallback() ? 'cloud' : 'offline';
        if (!silent) throw error;
      } finally {
        lastLocalTestAt = Date.now();
        emit();
      }
      return snapshot();
    })();

    localTestInFlight = run;
    try {
      return await run;
    } finally {
      localTestInFlight = null;
    }
  },

  async getLocalStatus() {
    try {
      const body = await localRequest('/v1/status', { method: 'GET' });
      return body || {};
    } catch (error) {
      if (mode() === SERVER_MODES.OFFLINE_SERVER_ONLY || mode() === SERVER_MODES.SQLITE_OFFLINE) throw error;
      return null;
    }
  },

  // A paired administrator may be using normal Firebase login (no employee
  // LAN session). Use their separately stored admin token for explicit sync;
  // employee devices continue to authenticate with their approved sessions.
  async syncNow() {
    const options = { method: 'POST', body: '{}', forceLocalProbe: true, requestTimeoutMs: 300000 };
    if (readAdminToken()) {
      try { return await adminRequest('/v1/sync/run', options); }
      catch (error) { if (error?.status !== 401 && error?.status !== 403) throw error; }
    }
    return localRequest('/v1/sync/run', options);
  },

  async getSyncStatus() {
    const options = { method: 'GET', forceLocalProbe: true };
    if (readAdminToken()) {
      try { return await adminRequest('/v1/sync/status', options); }
      catch (error) { if (error?.status !== 401 && error?.status !== 403) throw error; }
    }
    return localRequest('/v1/sync/status', options);
  },

  async retryFailed() {
    const options = { method: 'POST', body: '{}', forceLocalProbe: true, requestTimeoutMs: 300000 };
    if (readAdminToken()) {
      try { return await adminRequest('/v1/sync/retry', options); }
      catch (error) { if (error?.status !== 401 && error?.status !== 403) throw error; }
    }
    return localRequest('/v1/sync/retry', options);
  },

  async probeLocalServer(address) {
    const url = normalizeBaseUrl(address);
    if (!url) throw new Error('Enter the IP address of the computer running the offline server.');
    const browserWarning = checkLanBrowserSecurity(url);
    if (browserWarning) throw new Error(browserWarning);
    const response = await abortableFetch(`${url}/v1/discovery`, { method: 'GET' }, 4000);
    const discovered = await response.json().catch(() => ({}));
    if (!response.ok || discovered?.ok !== true || !discovered.serverId) {
      throw new Error('No compatible FrostFlow offline server was found at this IP (port 8787 by default).');
    }
    if (discovered.claimed && state.tenantId && discovered.tenantId && discovered.tenantId !== state.tenantId) {
      throw new Error('This LAN server is paired with another company.');
    }
    return { ...discovered, url };
  },

  async connectByIp(address) {
    const info = await this.probeLocalServer(address);
    if (!info.claimed) return { ...info, setupRequired: true, config: snapshot().config };
    // Login screens may not know the tenant until a claimed LAN server is
    // discovered. Never replace an existing tenant with a different one.
    if (!state.tenantId && info.tenantId) state.tenantId = String(info.tenantId);
    const previousUrl = normalizeBaseUrl(state.config.localServer?.url || '');
    const sameEndpoint = previousUrl === info.url;
    state.config = mergeConfig({
      ...state.config,
      localServer: {
        ...state.config.localServer,
        enabled: true,
        url: info.url,
        // Never carry a shared token to a different server IP by accident.
        token: '',
        storeId: String(info.storeId || ''),
        serverId: String(info.serverId || ''),
        serverName: String(info.serverName || ''),
      },
    });
    if (!sameEndpoint && hasWindow()) {
      try { window.localStorage.removeItem(LOCAL_AUTH_SESSION_KEY); } catch (_) {}
    }
    cache(); emit();
    let authenticated = false;
    try {
      const status = await localRequest('/v1/status', { method: 'GET', forceLocalProbe: true });
      authenticated = Boolean(status?.server?.claimed);
    } catch (error) {
      if (error.status !== 401 && error.status !== 403) throw error;
    }
    return { ...info, authenticated, loginRequired: !authenticated, config: snapshot().config };
  },

  hasLocalAdminAccess() { return Boolean(readAdminToken()); },

  async claimLocalServer({ url, setupCode, tenantId, storeId, serverName } = {}) {
    const base = normalizeBaseUrl(url);
    if (!base) throw new Error('Enter the offline server IP address.');
    if (!tenantId) throw new Error('A company must be selected before pairing.');
    const info = await this.probeLocalServer(base);
    if (info.claimed) throw new Error('This LAN server was already paired. Use Connect with IP, then sign in with an approved user account.');
    const device = deviceIdentity();
    const response = await abortableFetch(`${base}/v1/setup/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(device.id ? { 'X-Device-Id': device.id, 'X-Device-Name': device.name } : {}) },
      body: JSON.stringify({ setupCode, tenantId, storeId: storeId || `STORE-${String(tenantId).slice(0,12)}`, serverName: serverName || info.serverName }),
    }, 5000);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.message || `Local server claim failed with HTTP ${response.status}`);
    state.tenantId = String(tenantId || body.tenantId || state.tenantId || '');
    if (hasWindow() && body.adminToken) {
      try { window.localStorage.setItem(ADMIN_TOKEN_KEY, String(body.adminToken)); } catch (_) {}
    }
    keepLocalPairingToken(body.token, { tenantId: state.tenantId, serverId: body.serverId, serverUrl: base });
    state.config = mergeConfig({
      ...state.config,
      localServer: {
        ...state.config.localServer,
        enabled: true,
        url: base,
        token: '',
        serverId: String(body.serverId || state.config.localServer?.serverId || ''),
        storeId: String(body.storeId || storeId || state.config.localServer?.storeId || ''),
        serverName: String(body.serverName || serverName || state.config.localServer?.serverName || ''),
      },
    });
    cache();
    emit();
    return { ...body, config: state.config };
  },

  async bootstrapSameOrigin({ silent = true } = {}) {
    if (!hasWindow()) return snapshot();
    const origin = normalizeBaseUrl(window.location.origin || '');
    if (!origin) return snapshot();
    try {
      const response = await abortableFetch(`${origin}/health`, { method: 'GET' }, 1800);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.service !== 'frostflow-offline-server' || body?.claimed !== true) return snapshot();
      state.tenantId = String(body.tenantId || state.tenantId || '');
      // Discovering the same-origin LAN server must not silently change a fresh
      // browser from Online Only to Hybrid. Firebase remains the default until
      // the tenant's saved mode is loaded or a local offline session is created.
      const localSession = readLocalSession();
      const discoveredMode = state.config.serverMode === SERVER_MODES.OFFLINE_SERVER_ONLY
        ? SERVER_MODES.OFFLINE_SERVER_ONLY
        : (state.config.serverMode === SERVER_MODES.HYBRID || localSession?.sessionToken
          ? SERVER_MODES.HYBRID
          : SERVER_MODES.ONLINE_ONLY);
      state.config = mergeConfig({
        ...state.config,
        serverMode: discoveredMode,
        localServer: {
          ...state.config.localServer, enabled: true, url: origin, storeId: String(body.storeId || ''), serverId: String(body.serverId || ''), serverName: String(body.serverName || ''),
        },
      });
      cache(); emit();
      return snapshot();
    } catch (error) {
      if (!silent) throw error;
      return snapshot();
    }
  },

  async localAuthLogin(email, password, { sessionHours = 24 } = {}) {
    if (!isLocalConfigured()) await this.bootstrapSameOrigin({ silent: true });
    const local = state.config.localServer || {};
    const base = normalizeBaseUrl(local.url || (hasWindow() ? window.location.origin : ''));
    if (!base || !state.tenantId) throw new Error('Local offline server is not configured for this browser.');
    const device = deviceIdentity();
    const response = await abortableFetch(`${base}/v1/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(device.id ? { 'X-Device-Id': device.id, 'X-Device-Name': device.name } : {}) },
      body: JSON.stringify({ tenantId: state.tenantId, email: String(email || '').trim().toLowerCase(), password, sessionHours }),
    }, Math.max(3000, Number(local.timeoutMs || 3000)));
    const body = await response.json().catch(() => ({}));
    if (!response.ok) { const error = new Error(body?.message || 'Offline login failed.'); error.code = `local-auth/${body?.error || response.status}`; throw error; }
    const session = { sessionToken: body.sessionToken, expiresAt: body.expiresAt, user: body.user, tenantId: state.tenantId, serverUrl: base };
    // Successful LAN authentication is an explicit signal that this browser
    // should route company data through the local server. Preserve an explicit
    // Offline Server Only choice; otherwise enter Hybrid for safe cloud resync.
    if (state.config.serverMode !== SERVER_MODES.OFFLINE_SERVER_ONLY &&
        state.config.serverMode !== SERVER_MODES.SQLITE_OFFLINE) {
      state.config = mergeConfig({ ...state.config, serverMode: SERVER_MODES.HYBRID });
      cache();
      emit();
    }
    if (hasWindow()) {
      try { window.localStorage.setItem(LOCAL_AUTH_SESSION_KEY, JSON.stringify(session)); } catch (_) {}
    }
    try {
      await localRequest('/v1/status', { method: 'GET', forceLocalProbe: true });
      this.setRuntimeBackend('local', session?.user?.uid || session?.user?.id || ''); // Only after verified LAN authentication.
      state.activeSource = 'local';
      if (hasWindow()) window.dispatchEvent(new CustomEvent('coldstorage:local-auth-changed', { detail: session }));
      emit();
      return session;
    } catch (error) {
      if (error?.code === 'device-approval-required') {
        state.activeSource = 'offline';
        return { ...session, awaitingApproval: true, message: error.message };
      }
      throw error;
    }
  },

  getLocalAuthSession() {
    const session = readLocalSession();
    if (!session) return null;
    if ((state.tenantId && session.tenantId && state.tenantId !== session.tenantId) ||
      (state.config.localServer?.url && session.serverUrl && normalizeBaseUrl(state.config.localServer.url) !== normalizeBaseUrl(session.serverUrl))) {
      return null;
    }
    if (session.expiresAt && Date.parse(session.expiresAt) <= Date.now()) {
      if (hasWindow()) try { window.localStorage.removeItem(LOCAL_AUTH_SESSION_KEY); } catch (_) {}
      return null;
    }
    return session;
  },

  async localAuthLogout() {
    const session = readLocalSession();
    if (session?.sessionToken && isLocalConfigured()) {
      await localRequest('/v1/auth/logout', { method: 'POST', body: '{}', headers: { Authorization: `Bearer ${session.sessionToken}` } }).catch(() => {});
    }
    if (hasWindow()) {
      try { window.localStorage.removeItem(LOCAL_AUTH_SESSION_KEY); } catch (_) {}
      window.dispatchEvent(new CustomEvent('coldstorage:local-auth-changed', { detail: null }));
    }
  },

  async configureSyncCollections(collectionNames = []) {
    if (!isLocalConfigured()) return { skipped: true, collectionNames: [] };
    const options = {
      method: 'POST', forceLocalProbe: true,
      body: JSON.stringify({ collectionNames: Array.from(new Set((collectionNames || []).filter(Boolean))) }),
    };
    if (readAdminToken()) {
      try { return await adminRequest('/v1/sync/collections', options); }
      catch (error) { if (error?.status !== 401 && error?.status !== 403) throw error; }
    }
    return localRequest('/v1/sync/collections', options);
  },

  async importDocuments(documents = [], { tenantId = state.tenantId } = {}) {
    if (!Array.isArray(documents) || !documents.length || !localFirst() || !isLocalConfigured()) return { skipped: true, imported: 0 };
    let imported = 0;
    let skipped = 0;
    const chunkSize = 200;
    for (let index = 0; index < documents.length; index += chunkSize) {
      const chunk = documents.slice(index, index + chunkSize).map((row) => ({ path: row.path, data: safeJson(row.data), cloudUpdateTime: row.cloudUpdateTime || '' }));
      const result = await localRequest('/v1/firestore/import', { method: 'POST', body: JSON.stringify({ tenantId, documents: chunk }) });
      imported += Number(result?.imported || 0);
      skipped += Number(result?.skipped || 0);
    }
    return { imported, skipped };
  },

  async getAdminConfig() { return adminRequest('/v1/admin/config', { method: 'GET' }); },
  async updateAdminConfig(patch = {}) { return adminRequest('/v1/admin/config', { method: 'POST', body: JSON.stringify(safeJson(patch)) }); },
  async configureCloud({ projectId, storageBucket, serviceAccount } = {}) { return adminRequest('/v1/admin/cloud/config', { method: 'POST', body: JSON.stringify({ projectId, storageBucket, serviceAccount }) }); },
  async testCloud() { return adminRequest('/v1/admin/cloud/test', { method: 'POST', body: '{}' }); },
  async checkDatabase() { return adminRequest('/v1/admin/database/integrity', { method: 'POST', body: '{}' }); },
  async optimizeDatabase() { return adminRequest('/v1/admin/database/optimize', { method: 'POST', body: '{}' }); },
  async listBackups() { return adminRequest('/v1/admin/backups', { method: 'GET' }); },
  async createBackup({ includeFiles = false } = {}) { return adminRequest('/v1/admin/backups/create', { method: 'POST', body: JSON.stringify({ includeFiles: Boolean(includeFiles) }) }); },
  async restoreBackup(id, { restoreFiles = true } = {}) { return adminRequest('/v1/admin/backups/restore', { method: 'POST', body: JSON.stringify({ id, restoreFiles: Boolean(restoreFiles) }) }); },
  async listConflicts(status = 'open') { return adminRequest(`/v1/admin/conflicts?status=${encodeURIComponent(status)}`, { method: 'GET' }); },
  async resolveConflict(id, resolution, mergedData = null) { return adminRequest(`/v1/admin/conflicts/${encodeURIComponent(id)}/resolve`, { method: 'POST', body: JSON.stringify({ resolution, mergedData: safeJson(mergedData) }) }); },
  async listDevices() { return adminRequest('/v1/admin/devices', { method: 'GET' }); },
  async setDeviceStatus(id, status) { return adminRequest(`/v1/admin/devices/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ status }) }); },
  async getLogs(limit = 200) { return adminRequest(`/v1/admin/logs?limit=${Math.max(1, Number(limit) || 200)}`, { method: 'GET' }); },
  async restartLocalServer() { return adminRequest('/v1/admin/service/restart', { method: 'POST', body: '{}' }); },
  async listOfflineUsers() { return adminRequest('/v1/admin/offline-users', { method: 'GET' }); },
  async provisionOfflineUser(payload = {}) { return adminRequest('/v1/admin/offline-users/provision', { method: 'POST', body: JSON.stringify(safeJson(payload)) }); },
  async setOfflineUserEnabled(id, enabled) { return adminRequest(`/v1/admin/offline-users/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ enabled: Boolean(enabled) }) }); },
  async getWebBuildStatus() { return adminRequest('/v1/admin/web/status', { method: 'GET' }); },
  async deployWebBuild(file) {
    if (!file) throw new Error('React production build ZIP is required.');
    return adminRequest('/v1/admin/web/deploy', { method: 'PUT', body: file, headers: { 'Content-Type': 'application/zip' } });
  },
  async uploadFile({ storagePath, file, metadata = {} } = {}) {
    if (!file || !storagePath) throw new Error('File and storage path are required.');
    const meta = typeof btoa === 'function'
      ? btoa(unescape(encodeURIComponent(JSON.stringify(safeJson(metadata || {})))))
      : '';
    return localRequest(`/v1/files/upload?path=${encodeURIComponent(storagePath)}`, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', ...(meta ? { 'X-File-Meta': meta } : {}) },
    });
  },
  async deleteFile(storagePath) {
    if (!storagePath) throw new Error('Storage path is required.');
    return localRequest(`/v1/files/delete?path=${encodeURIComponent(storagePath)}`, { method: 'DELETE' });
  },

  async writeDocument({ path, action = 'set', data = null, merge = false, operationId = '' }) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/write', {
        method: 'POST',
        body: JSON.stringify({
          operationId: operationId || undefined,
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          path,
          action,
          merge: Boolean(merge),
          data: safeJson(data),
          client: 'react',
          createdAt: nowIso(),
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (canCloudFallback()) {
        state.activeSource = 'cloud';
        emit();
        return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      }
      state.activeSource = 'offline';
      emit();
      throw error;
    }
  },

  async batchWrite(operations = [], { preconditions = [] } = {}) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/batch', {
        method: 'POST',
        body: JSON.stringify({
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          operations: operations.map((op) => ({ ...op, data: safeJson(op.data) })),
          preconditions: preconditions.map((row) => ({ path: row.path, version: Number(row.version || 0) })),
          client: 'react',
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (error?.code === 'transaction-conflict') throw error;
      if (canCloudFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async readDocument(path) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest(`/v1/firestore/document?path=${encodeURIComponent(path)}`, { method: 'GET' });
      state.activeSource = 'local';
      return { handled: true, found: body?.found !== false, data: reviveJson(body?.data || null), version: Number(body?.version || 0), source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async queryDocuments(spec = {}) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/query', {
        method: 'POST',
        body: JSON.stringify({ ...safeJson(spec), tenantId: state.tenantId }),
      });
      state.activeSource = 'local';
      return { handled: true, rows: Array.isArray(body?.rows) ? body.rows.map((row) => ({ ...row, data: reviveJson(row?.data || row?.payload || {}) })) : [], source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },
};

export { safeJson, reviveJson, mergeConfig };
export default service;
